import { alphaSort } from 'vet-bones/utils';

import { ModelConceptClass } from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  DESIGNER_COLOR_ID_IRI,
  OWL_CLASS_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
  RDFS_SUB_CLASS_OF_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { getAbbreviationOrFullIri } from 'src/utils/getAbbreviationOrFullIri';

const modelConceptClassSort = alphaSort(
  (item: ModelConceptClass) => item.value
);

export const stringifyModelConceptClasses = (
  model: ModelSnapshot,
  namespaces: Record<string, string>,
  filter?: (conceptClass: ModelConceptClass) => boolean
): string => {
  const abbreviate = (iri: string) => getAbbreviationOrFullIri(iri, namespaces);

  const hierarchyByIri = model.linkDataArray
    .filter(isModelConceptHierarchy)
    .reduce<Record<string, Set<string>>>((acc, data) => {
      const from = getModelConceptClassValue(data.from);
      const to = getModelConceptClassValue(data.to);
      if (!acc[from]) {
        acc[from] = new Set([to]);
      } else {
        acc[from].add(to);
      }
      return acc;
    }, {});

  let classes = model.nodeDataArray.filter(isModelConceptClass);

  if (filter) {
    classes = classes.filter(filter);
  }

  return classes
    .sort(modelConceptClassSort)
    .map(({ value }) => {
      const abbreviated = abbreviate(value);

      const lines = [`${abbreviated} a ${abbreviate(OWL_CLASS_IRI)}`];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(
          `${abbreviate(RDFS_LABEL_IRI)} "${escapeDoubleQuotes(label)}"`
        );
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `${abbreviate(RDFS_COMMENT_IRI)} "${escapeDoubleQuotes(description)}"`
        );
      }

      const colorId = model.modelData.colorIdsByIri[value];
      if (colorId) {
        lines.push(
          `${abbreviate(DESIGNER_COLOR_ID_IRI)} "${escapeDoubleQuotes(
            colorId
          )}"`
        );
      }

      const hierarchy = Array.from(hierarchyByIri[value] || []);
      if (hierarchy.length) {
        const objects = hierarchy
          .map((object) => abbreviate(object))
          .join(', ');
        lines.push(`${abbreviate(RDFS_SUB_CLASS_OF_IRI)} ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
